"use client";

import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { CheckCircleIcon, FadeIn } from "staysure-component-library";

import RichText from "@/components/atoms/rich-text/rich-text";
import ToggleBtnLabelWrapper from "@/components/atoms/toggle-btn-wrapper/toggle-btn-label-wrapper";
import { useRenewalReviewStateContext } from "@/providers/renewal-review-state-provider";
import { RenewalContactPreferencesContentType } from "@/schema/renewal-page/review-page.schema";
import { RootState } from "@/store/root-reducer";
import { brand } from "@/tailwind.config";
import { MarketingConsent } from "@/types/MarketingConsent";

type RenewalContactPreferencesProps = {
  content: RenewalContactPreferencesContentType;
};
type ContactMethod = "email" | "sms" | "telephone" | "post";

function RenewalContactPreferences({
  content,
  isMobile,
}: Readonly<RenewalContactPreferencesProps & { isMobile?: boolean }>) {
  const { contactPreferences, contactPreferenceLabels } = useMemo(() => {
    return {
      contactPreferences: [
        (content?.ctaLabelOneId as ContactMethod) ?? "email",
        (content?.ctaLabelTwoId as ContactMethod) ?? "sms",
        (content?.ctaLabelThreeId as ContactMethod) ?? "telephone",
        (content?.ctaLabelFourId as ContactMethod) ?? "post",
      ],
      contactPreferenceLabels: [
        content?.ctaLabelOne,
        content?.ctaLabelTwo,
        content?.ctaLabelThree,
        content?.ctaLabelFour,
      ],
    };
  }, [content]);

  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes, referenceId } = quoteData;
  const [isPreferenceClicked, setIsPreferenceClicked] = React.useState(false);

  const { setMarketingConsent, marketingConsent } =
    useRenewalReviewStateContext();

  const updatePreferences = (preference: ContactMethod, value: boolean) => {
    setMarketingConsent({
      ...marketingConsent,
      [preference]: value,
    } as unknown as MarketingConsent);
  };

  useEffect(() => {
    const marketingConsentData = attributes.marketingConsent || null;

    if (marketingConsentData) {
      const marketingConsentDataUpdate = {
        [contactPreferences[0]]:
          marketingConsentData[contactPreferences[0]] ?? true,
        [contactPreferences[1]]:
          marketingConsentData[contactPreferences[1]] ?? true,
        [contactPreferences[2]]:
          marketingConsentData[contactPreferences[2]] ?? true,
        [contactPreferences[3]]:
          marketingConsentData[contactPreferences[3]] ?? true,
      };
      setMarketingConsent(
        marketingConsentDataUpdate as unknown as MarketingConsent
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  useEffect(() => {
    const preference = document.getElementById(
      "contact-page-preferences-hyperlink"
    );
    const handleClick = (event: MouseEvent) => {
      event.preventDefault();
      setIsPreferenceClicked((prev) => !prev);
    };

    if (preference) {
      preference.onclick = handleClick;
    }
  }, [referenceId]);

  if (!referenceId) return "loading...";

  return (
    <div data-testid="renewal-contact-preferences">
      <h2 className="mb-spacing-xs font-header-font text-m md:text-3xl md:leading-custom-size-xl leading-[28px] font-[400] text-text-title">
        {content.conatctPreferencesTitle}
      </h2>
      <RichText
        richText={content.contactPreferencesDescription}
        className="font-body-font text-2xs leading-6 text-text-body font-[400] [&_a]:!break-normal"
      />

      <FadeIn
        isVisible={isPreferenceClicked}
        data-testid="preferences-fade-in-animation"
        maxHeight={isMobile ? "400px" : "200px"}
        config={{ tension: 200, friction: isMobile ? 30 : 25 }}
      >
        <div className="grid md:grid-cols-4 grid-cols-1 gap-spacing-xs mt-spacing-l">
          {contactPreferences.map((preference, index) => (
            <div key={`preference ${index as unknown as string}`}>
              <ToggleBtnLabelWrapper
                id={`contact-preference-${index}`}
                btnType="button"
                label={contactPreferenceLabels[index] as unknown as string}
                isDefaultActive={marketingConsent[preference]}
                onClick={(e: boolean) => {
                  updatePreferences(preference, e);
                }}
                isActiveRightIcon
                activeRightIcon={
                  <CheckCircleIcon
                    brand={brand === "expat" ? "staysure" : "avanti"}
                    semanticColor="dark"
                  />
                }
                className="border-[1px] border-interactive-secondary-default text-[16px]
             text-text-on-primary w-full h-[56px] rounded-lg font-semibold font-body-font
              py-spacing-s md:px-spacing-xs lg:px-spacing-s px-spacing-s flex flex-col justify-center items-start
             gap-grid-system-space-050 leading-[24px]"
              />
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  );
}

export default RenewalContactPreferences;
